import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PageHeader, Section, StatBadge } from "@/components/Chrome";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function Approvals() {
  const qc = useQueryClient();
  const { data: pending = [] } = useQuery({ queryKey: ["approvals", "pending"], queryFn: () => api.approvals.list("pending"), refetchInterval: 4000 });
  const { data: history = [] } = useQuery({ queryKey: ["approvals", "all"], queryFn: () => api.approvals.list(), refetchInterval: 6000 });

  const decide = useMutation({
    mutationFn: ({ id, decision }) => api.approvals.decide(id, decision),
    onSuccess: (_, v) => { qc.invalidateQueries({ queryKey: ["approvals"] }); qc.invalidateQueries({ queryKey: ["activity"] }); toast.success(`${v.decision === "approve" ? "Approved" : "Rejected"}`); },
  });

  const resolved = history.filter(a => a.status !== "pending");

  return (
    <div data-testid="approvals-page">
      <PageHeader eyebrow="Human-in-the-loop" title="Pending approvals" subtitle="Copilot never executes a high-stakes action without your yes. You can also respond in-line on Telegram." />
      <Section title={`Waiting on you (${pending.length})`}>
        {pending.length === 0 ? (
          <div className="text-sm text-stone-500">Nothing waiting.</div>
        ) : (
          <ul className="space-y-3" data-testid="approvals-pending">
            {pending.map(a => (
              <li key={a.id} data-testid={`approval-${a.id}`}
                  className="border border-stone-800 hover:border-orange-700 transition-colors">
                <div className="px-5 py-4 border-b border-stone-800/70 flex items-center gap-3">
                  <StatBadge status="accent">{a.action}</StatBadge>
                  <span className="text-sm text-stone-100">{a.label}</span>
                  <span className="ml-auto font-mono text-[10px] text-stone-500">{new Date(a.ts).toLocaleString()}</span>
                </div>
                <div className="px-5 py-4 text-sm text-stone-300">{a.payload?.instruction}</div>
                <div className="px-5 py-3 border-t border-stone-800/70 flex gap-2">
                  <button data-testid={`approve-${a.id}`}
                    onClick={() => decide.mutate({ id: a.id, decision: { decision: "approve" } })}
                    className="text-[10px] uppercase tracking-widest font-mono px-4 py-2 bg-green-700 hover:bg-green-600 text-white transition-colors">
                    Approve
                  </button>
                  <button data-testid={`reject-${a.id}`}
                    onClick={() => decide.mutate({ id: a.id, decision: { decision: "reject" } })}
                    className="text-[10px] uppercase tracking-widest font-mono px-4 py-2 border border-stone-700 text-stone-300 hover:border-red-700 hover:text-red-400 transition-colors">
                    Reject
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section title={`History (${resolved.length})`}>
        {resolved.length === 0 ? (
          <div className="text-sm text-stone-500">Nothing yet.</div>
        ) : (
          <ul className="divide-y divide-stone-800/70" data-testid="approvals-history">
            {resolved.map(a => (
              <li key={a.id} className="py-3 grid grid-cols-12 gap-3 items-center">
                <span className="col-span-2 font-mono text-[11px] text-stone-500">{new Date(a.resolved_at || a.ts).toLocaleString()}</span>
                <span className="col-span-2"><StatBadge status={a.status === "approved" ? "success" : "warning"}>{a.status}</StatBadge></span>
                <span className="col-span-3 text-sm text-stone-200">{a.label}</span>
                <span className="col-span-5 text-xs text-stone-500 truncate">{a.payload?.instruction}</span>
              </li>
            ))}
          </ul>
        )}
      </Section>
    </div>
  );
}
